import Text from 'components/elements/texts/Text'
import { CLIENT_SETTINGS } from 'constants/clientSettings'
import { HistoryStackContext } from 'contexts/HistoryStackContext'
import React, { useCallback, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { transitionWithAction } from 'utils/commonUtils'
import { StatusProps } from 'utils/status'

const withChangeLink = (
  item: ConfirmLineProps
): item is ConfirmLinePropsWithChangeLink => !item.isRequest

const withDeleteLink = (item: ConfirmLineProps): item is ConfirmLineProps =>
  !item.isRequest && !!item.onDeleteItem

/**
 * 確認項目
 * 編集画面で利用する場合にはタイトルが通常文字になる
 * 確認画面で利用する場合にはタイトルが太文字になる
 * 要請後画面で利用する場合には変更リンクを消す
 */
const ConfirmLine: React.FC<ConfirmLineProps> = (props) => {
  const history = useHistory()
  const historyStackContext = useContext(HistoryStackContext)

  const linkClick = useCallback((): void => {
    if (withChangeLink(props)) {
      transitionWithAction(
        historyStackContext,
        props.titleLabel,
        () => {
          history.push(props.changeLinkTo)
        },
        props.changeLinkAction
      )
    }
  }, [props.titleLabel, props.changeLinkTo])

  return (
    <React.Fragment>
      <TopArea isConfirm={props.isConfirm}>
        <Text bold={props.isConfirm}>{props.titleLabel}</Text>
        {withChangeLink(props) && (
          <ChangeTextArea onClick={linkClick}>
            <ChangeText color={CLIENT_SETTINGS.colors.main}>変更</ChangeText>
          </ChangeTextArea>
        )}
      </TopArea>
      <BottomArea>
        <ContentArea>{props.content}</ContentArea>
        {withDeleteLink(props) && (
          <DeleteTextArea onClick={props.onDeleteItem}>
            <DeleteText color={CLIENT_SETTINGS.colors.main}>
              {props.deleteBtnLabel}
            </DeleteText>
          </DeleteTextArea>
        )}
      </BottomArea>
    </React.Fragment>
  )
}

export type ConfirmLineProps = {
  isConfirm?: boolean
  isRequest?: boolean
  titleLabel: string
  content: React.ReactNode
  changeLinkTo?: string
  onDeleteItem?: () => void
  deleteBtnLabel?: string
}

type ConfirmLinePropsWithChangeLink = ConfirmLineProps & {
  changeLinkTo: string
  changeLinkAction?: () => StatusProps | Promise<StatusProps>
}

const TopArea = styled.div<{ isConfirm?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-left: 16px;
  ${(props): string =>
    props.isConfirm
      ? `
    margin-top: 24px;
    margin-right: 24px;
    margin-bottom: 16px;
  `
      : `
    margin-top: 12px;
    margin-right: 16px;
    margin-bottom: 12px;
      `}
`
const ContentArea = styled.div`
  margin-right: 18px;
  margin-left: 18px;
`

const ChangeTextArea = styled.div`
  font-weight: 600;
  text-align: right;
`

const ChangeText = styled(Text)`
  font-size: 17px;
`

const DeleteTextArea = styled.div`
  margin-right: 18px;
  text-align: right;
`

const DeleteText = styled(Text)`
  font-size: 17px;
`

const BottomArea = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
  margin-bottom: 24px;
`

export default React.memo<ConfirmLineProps>(ConfirmLine)
